<template>
  <transition name="slide-down" v-if="lgAndUp">
    <div
      v-if="isVisible"
      class="reportCompareBar"
      :class="{ expanded: selectedReports.length > 0 }"
    >
      <v-btn
        color="secondary"
        variant="text"
        size="x-small"
        append-icon="mdi-close"
        class="close-btn"
        @click="closeCompare"
      >
        Close
      </v-btn>
      <v-container class="position-relative w-100 h-100">
        <!-- Main header content -->
        <v-row class="d-flex justify-center align-center header-row">
          <v-col cols="4">
            <h2>Compare</h2>
            <div class="text-body-1">
              {{ selectedReports.length }} of 3 selected
            </div>
          </v-col>
          <v-col cols="4">
            Select up to 3 products to make a quick comparison.
          </v-col>
          <v-col cols="4" align="right">
            <v-btn
              :disabled="selectedReports.length < 2"
              variant="flat"
              color="primary"
              size="large"
              @click="handleCompare"
            >
              Compare Products
            </v-btn>
          </v-col>
        </v-row>

        <!-- Items content with simple fade -->
        <div v-show="selectedReports.length > 0" class="items-container">
          <v-row class="compare-items-row">
            <v-col v-for="(item, index) in 3" :key="index" cols="4">
              <div v-if="selectedReports[index]" class="compare-item">
                <div class="compare-item-content">
                  <div class="compare-item-left">
                    <div
                      class="compare-item-image"
                      :style="{
                        backgroundImage: getReportCoverImageUrl(
                          selectedReports[index].report,
                        ),
                      }"
                    ></div>
                  </div>
                  <div class="compare-item-right">
                    <div class="compare-item-title">
                      {{ selectedReports[index].report.title }}
                    </div>
                    <div class="compare-item-details">
                      <div class="compare-item-details-item">
                        <div class="text-caption">SUBJECT</div>
                        <div
                          class="text-body-2 text-secondary font-weight-bold"
                        >
                          {{ selectedReports[index].report.subject.name }}
                        </div>
                      </div>
                      <div class="compare-item-details-item">
                        <div class="text-caption">GRADES</div>
                        <div
                          class="text-body-2 text-secondary font-weight-bold"
                        >
                          {{
                            formatGradeDisplay(
                              selectedReports[index].report.grades,
                            )
                          }}
                        </div>
                      </div>
                      <div class="compare-item-details-item">
                        <div class="text-caption">RELEASED</div>
                        <div
                          class="text-body-2 text-secondary font-weight-bold"
                        >
                          {{
                            new Date(
                              selectedReports[index].report.release * 1000,
                            ).toLocaleDateString()
                          }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="compare-item-overlay">
                    <v-btn
                      icon
                      size="small"
                      color="white"
                      class="remove-btn"
                      @click="removeReport(index)"
                    >
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>
              <div v-else class="compare-placeholder">
                <div class="text-secondary">
                  Select another product to compare
                </div>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-container>
    </div>
  </transition>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue';
  import { useCompareStore } from '../../store';
  import { useDisplay } from 'vuetify';

  const display = useDisplay();
  const lgAndUp = computed(() => display.lgAndUp.value);
  const compareStore = useCompareStore();
  const isVisible = ref(true);

  const selectedReports = computed(() => compareStore.selectedReports);

  // Watch for changes in selectedReports to control visibility
  watch(
    () => selectedReports.value.length,
    (newLength: number) => {
      // Make the bar visible again if items are added
      if (newLength > 0) {
        isVisible.value = true;
      }
    },
  );

  // Helper function to format grade display
  function formatGradeDisplay(
    grades?: Array<{
      name: string;
      title?: string;
      order?: number;
      slug?: string;
    }>,
  ) {
    if (!grades || !grades.length) return 'N/A';

    // Map grade names to their simplified form
    const formattedGrades = grades.map((grade) => {
      // Handle special case for Kindergarten
      if (
        grade.name === '0' ||
        grade.name === 'K' ||
        grade.name?.toLowerCase().includes('kindergarten')
      ) {
        return 'K';
      }
      // For other grades, just return the numeric value
      return grade.name?.replace(/[^0-9]/g, '') || grade.name;
    });

    // Sort grades to ensure proper order
    formattedGrades.sort((a: string, b: string) => {
      if (a === 'K') return -1;
      if (b === 'K') return 1;
      return parseInt(a) - parseInt(b);
    });

    // Return a single grade or range
    if (formattedGrades.length === 1) {
      return formattedGrades[0];
    } else {
      return `${formattedGrades[0]}-${formattedGrades[formattedGrades.length - 1]}`;
    }
  }

  // Helper function to get report cover image URL with fallback
  function getReportCoverImageUrl(report: any): string {
    if (!report?.cover?.uri) {
      return 'url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAzMDAgNDAwIj48cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjQwMCIgZmlsbD0iI2ViZjdmYiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjQiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiMwMDdlYWIiPlJlcG9ydDwvdGV4dD48L3N2Zz4=)';
    }
    return `url(${report.cover.uri})`;
  }

  const closeCompare = () => {
    isVisible.value = false;
    // Clear all selected reports when closing the bar
    compareStore.clearReports();
  };

  const removeReport = (index: number) => {
    compareStore.removeReport(selectedReports.value[index].id);
  };

  const handleCompare = () => {
    // TODO: Implement compare functionality
  };
</script>

<style scoped lang="scss">
  .reportCompareBar {
    z-index: 1000;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 120px; /* Base height */
    background-color: rgb(var(--v-theme-grey-blue));
    transition: height 0.4s cubic-bezier(0.25, 0.8, 0.5, 1);
    overflow: hidden;

    &.expanded {
      height: 300px; /* Expanded height */
    }
  }

  .w-100 {
    width: 100%;
  }

  .h-100 {
    height: 100%;
  }

  .header-row {
    padding-top: 16px;
    padding-bottom: 16px;
    min-height: 120px;
  }

  .items-container {
    opacity: 1;
    transition: opacity 0.3s ease-in-out;
    margin-bottom: 16px;
  }

  /* Slide down transition for the entire bar */
  .slide-down-enter-active,
  .slide-down-leave-active {
    transition: transform 0.3s ease-out;
  }

  .slide-down-enter-from,
  .slide-down-leave-to {
    transform: translateY(100%);
  }

  .close-btn {
    z-index: 1001;
    cursor: pointer;
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    top: 15px;
    right: 15px;
    height: auto !important;
    font-weight: bold !important;

    :deep(.v-icon) {
      font-size: 14px;
      color: rgb(var(--v-theme-secondary));
      margin: 0 !important;
    }
  }

  .compare-placeholder {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 148px;
    background-color: rgba(255, 255, 255, 0.5);
    border-radius: 4px;
    border: 2px dashed rgba(var(--v-theme-blue), 0.25);
    color: rgb(var(--v-theme-text-secondary));
    font-size: 12px;
    .text-secondary {
      cursor: default;
    }
  }

  .compare-items-row {
    overflow: visible;
  }

  .compare-item {
    position: relative;
    width: 100%;
    height: 148px;
    background-color: white;
    border-radius: 3px;
    border: 2px solid rgba(var(--v-theme-blue), 0.25);
    overflow: visible;
    &:hover {
      border: 2px solid rgba(var(--v-theme-blue), 0.5);
    }

    .compare-item-content {
      display: flex;
      flex-direction: row;
      height: 100%;
      padding: 16px;
      justify-content: space-between;
      overflow: hidden;
    }

    .compare-item-left {
      margin-right: 24px;

      .compare-item-image {
        display: flex;
        width: 80px;
        height: 112px;
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        border: 1px solid #cccccc;
        flex-shrink: 0;
      }
    }

    .compare-item-right {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      flex: 1;
    }

    .compare-item-title {
      padding: 12px 0 0 0;
      color: rgb(var(--v-theme-secondary));
      font-family: Rockwell;
      font-size: 14px !important;
      font-weight: normal !important;
    }

    .compare-item-details {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      color: rgb(var(--v-theme-text-secondary));
      font-size: 10px;
      margin-top: auto;
      gap: 12px;

      .compare-item-details-item {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        max-width: 90px;

        &:nth-child(2) {
          margin: 0 auto;
        }

        &:last-child {
          margin-left: auto;
        }
      }

      .text-caption {
        color: rgb(var(--v-theme-text-secondary));
        margin-bottom: 2px;
      }

      .text-body-2 {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        width: 100%;
      }

      .text-secondary {
        font-size: 14px !important;
      }
    }

    .compare-item-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      padding: 8px;
      transition: opacity 0.2s ease;
      pointer-events: none;
      overflow: visible;

      .remove-btn {
        position: absolute;
        top: -12px;
        right: 8px;
        height: 24px !important;
        width: 24px !important;
        background: rgb(var(--v-theme-blue));
        border-radius: 100%;
        pointer-events: auto;
        z-index: 10;

        :deep(.v-icon) {
          font-size: 18px;
          font-weight: bold;
          color: white;
          margin: 0 !important;
        }
      }
    }
  }
</style>
